"use client";
import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { Link, useRouter, usePathname } from "@/i18n/navigation";
import dynamic from "next/dynamic";
import styles from "./header.module.css";
import Image from "next/image";
import { MainData } from "@/components/common/CommonArray/FileArray";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { routing } from "@/i18n/routing";
import type { LucideIcon } from "lucide-react";
// import posthog from 'posthog-js'
import {
  Maximize2,
  SquareRadical,
  Calculator,
  Scale,
  Percent,
  Banknote,
  Briefcase,
  GraduationCap,
  Home,
  Clock,
  Ruler,
  Weight,
  Box,
  Thermometer,
  Gauge,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  PenSquare,
  Music,
  Folder,
  Type,
  FileText,
  Server,
  Contact,
  DollarSign,
  Globe,
  Mail,
  Bitcoin,
  Languages,
  FileCode,
  HelpCircle,
  Shield,
} from "lucide-react";
// import { PositiveTag } from "@/components/common/PositiveTag/PositiveTag";

const CALC_MENU_ITEMS: { name: string; href: string; icon: LucideIcon }[] = [
  { name: "Full Screen Calc", href: "/full-screen-calculator", icon: Maximize2 },
  { name: "Scientific Calc", href: "/scientific-calculator", icon: SquareRadical },
  { name: "Age Calc", href: "/age-calculator", icon: Calculator },
  { name: "BMI Calc", href: "/bmi-calculator", icon: Scale },
  { name: "Percentage Calc", href: "/percentage-calculator", icon: Percent },
  { name: "Loan Calc", href: "/loan-calculator", icon: Banknote },
  { name: "Salary Calc", href: "/salary-calculator", icon: Briefcase },
  { name: "Log Calc", href: "/log(logarithm)-calculator", icon: SquareRadical },
  { name: "CGPA/CPI Calc", href: "/cpi-cgpa-to-percentage-calculator", icon: GraduationCap },
  { name: "Mortgage Calc", href: "/mortgage-calculator", icon: Home },
  { name: "Timesheet Calc", href: "/timesheet-calculator", icon: Clock },
  { name: "GST Calc", href: "/gst-calculator", icon: Percent },
  { name: "Length Converter", href: "/length-convertor", icon: Ruler },
  { name: "Weight Converter", href: "/weight-convertor", icon: Weight },
  { name: "Volume Converter", href: "/volume-convertor", icon: Box },
  { name: "Temperature Converter", href: "/temprature-convertor", icon: Thermometer },
  { name: "Time Converter", href: "/time-convertor", icon: Clock },
  { name: "Speed Converter", href: "/speed-convertor", icon: Gauge },
];

const Select = dynamic(() => import("react-select"), { 
  ssr: false,
  loading: () => <div className={`result-search-box ${styles.searchLoadingPlaceholder}`} />
});

type HeaderProps = { minimal?: boolean };

export default function Header({ minimal = false }: HeaderProps) {
  const t = useTranslations("common");
  const locale = useLocale();
  const pathname = usePathname();
  const [dropdownMobileOpen, setDropdownMobileOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [calcDropdownOpen, setCalcDropdownOpen] = useState(false);
  const router = useRouter();
  const [mobileHeaderOpen, setMobileHeaderOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const calcDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (minimal) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (langDropdownRef.current && !langDropdownRef.current.contains(target)) {
        setLangDropdownOpen(false);
      }
      if (calcDropdownRef.current && !calcDropdownRef.current.contains(target)) {
        setCalcDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [minimal]);

  if (minimal) {
    return (
      <div className={styles.headerContainerMinimal}>
        <div className={styles.headerAlignmentMinimal}>
          <Link href="/" className={styles.headerLogoMinimal} aria-label={t("siteName")}>
            <Image
              src={"/Images/convertor-logo-sm.webp"}
              title="master sm logo"
              alt="Tiny Tools"
              width={36}
              height={36}
              sizes="36px"
              priority
            />
          </Link>
        </div>
      </div>
    );
  }

  const getLocaleLabel = (loc: string) =>
    t(loc === "en" ? "english" : loc === "hi" ? "hindi" : "chinese");

  const localeOptions = routing.locales.map((loc) => ({
    value: loc,
    label: getLocaleLabel(loc),
  }));
  const handleOnSelect = (item: { value: string }) => {
    router.push(item?.value);
  };
  const options = MainData?.map((item: any, index: number) => ({
    value: item?.navigate,
    label: item?.Name,
    id: item?.id || index,
  }));

  const customStyles = {
    placeholder: (provided: any) => ({
      ...provided,
      color: "#6b7280",
    }),
    container: (base: any) => ({
      ...base,
      width: "100%",
      maxWidth: "400px",
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: "14px",
      borderColor: state.isFocused ? "rgba(102, 126, 234, 0.4)" : "rgba(102, 126, 234, 0.12)",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(102, 126, 234, 0.1)"
        : "0 1px 2px rgba(102, 126, 234, 0.04)",
      padding: "6px 4px 6px 16px",
      minHeight: "46px",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        borderColor: "rgba(102, 126, 234, 0.25)",
        boxShadow: "0 2px 8px rgba(102, 126, 234, 0.06)",
      },
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: "rgba(102, 126, 234, 0.6)",
      "&:hover": { color: "#667eea" },
    }),
    input: (provided: any) => ({
      ...provided,
      color: "#26274b",
      fontSize: "15px",
    }),
  };

  const handleChange = (selectedOption: any) => {
    if (selectedOption) {
      // posthog.capture('search_tool_selected', {
      //   tool_name: selectedOption.label,
      //   tool_href: selectedOption.value,
      // });
      handleOnSelect(selectedOption);
    }
  };

  return (
    <>
      <div className={styles.headerContainer}>
        <div className={styles.headerAlignment}>
          <Link href="/" className={styles.headerLogo}>
            <Image
              src={"/Images/convertor-logo-sm.webp"}
              title="master sm logo"
              alt="Tiny Tools"
              width={50}
              height={50}
              sizes="50px"
              priority
            />
            <span className={styles.headerSiteName}>{t("siteName")}</span>
          </Link>
          <div className={styles.headerSearchWrap}>
            <Select
              components={{
                IndicatorSeparator: () => null,
              }}
              aria-label="search-tools"
              options={options}
              className={classNames("result-search-box", styles.headerSearch)}
              classNamePrefix="select"
              placeholder={t("searchPlaceholder")}
              onChange={handleChange}
              styles={customStyles}
              isSearchable
            />
            {/* <PositiveTag /> */}
          </div>
          <nav className={styles.menuSearch} aria-label="Main navigation">
            <div className={styles.menuText}>
              <Link href="/draw" className={styles.headerCtaPrimary}
              //  onClick={() => {
              //   posthog.capture('draw_tool_opened', { source: 'header_nav' });
              // }}
              >
                {t("draw")}
              </Link>
              <div
                className={styles.calcDropdownWrap}
                ref={calcDropdownRef}
              >
                <button
                  type="button"
                  className={styles.calcDropdownTrigger}
                  onClick={() => {
                    setCalcDropdownOpen(!calcDropdownOpen);
                    setLangDropdownOpen(false);
                  }}
                  aria-expanded={calcDropdownOpen}
                  aria-haspopup="menu"
                  aria-label={t("calc")}
                >
                  <Calculator size={16} aria-hidden />
                  <span>{t("calc")}</span>
                  {calcDropdownOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                </button>
                <div
                  className={classNames(styles.calcDropdownPanel, {
                    [styles.calcDropdownPanelOpen]: calcDropdownOpen,
                  })}
                  role="menu"
                  aria-label={t("calc")}
                >
                  <div className={styles.calcDropdownTitle}>{t("calc")}</div>
                  <div className={styles.calcGrid}>
                    {CALC_MENU_ITEMS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={styles.calcButton}
                          onClick={() => {
                            setCalcDropdownOpen(false);
                            // posthog.capture('calculator_selected', {
                            //   calculator_name: item.name,
                            //   calculator_href: item.href,
                            // });
                          }}
                          role="menuitem"
                        >
                          <Icon size={16} aria-hidden />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
              <Link href="/privacy-policy">{t("privacyPolicy")}</Link>
              <Link href="/terms-condition">{t("termsOfService")}</Link>
              <Link href="/faq">{t("faq")}</Link>
              <Link href="/contact">{t("contactUs")}</Link>
              <div
                className={styles.langDropdownWrap}
                ref={langDropdownRef}
              >
                <button
                  type="button"
                  className={styles.langDropdownTrigger}
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  aria-expanded={langDropdownOpen}
                  aria-haspopup="listbox"
                  aria-label={t("language")}
                >
                  <span>{getLocaleLabel(locale)}</span>
                  {langDropdownOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                </button>
                <div
                  className={classNames(styles.langDropdownMenu, {
                    [styles.langDropdownMenuOpen]: langDropdownOpen,
                  })}
                  role="listbox"
                  aria-label={t("language")}
                >
                  {localeOptions.map((opt) => (
                    <Link
                      key={opt.value}
                      href={pathname || "/"}
                      locale={opt.value}
                      className={classNames(styles.langDropdownItem, {
                        [styles.langDropdownItemActive]: opt.value === locale,
                      })}
                      onClick={() => {
                        setLangDropdownOpen(false);
                        // if (opt.value !== locale) {
                        //   posthog.capture('language_changed', {
                        //     from_locale: locale,
                        //     to_locale: opt.value,
                        //   });
                        // }
                      }}
                      role="option"
                      aria-selected={opt.value === locale}
                    >
                      {opt.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
          <div
            className={styles.mobileMenu}
            onClick={() => setMobileHeaderOpen(!mobileHeaderOpen)}
          >
            <Menu size={20} />
          </div>
        </div>
      </div>
      <div
        className={
          mobileHeaderOpen
            ? classNames(styles.mobileViewHeader, styles.mobileHeaderShow)
            : classNames(styles.mobileViewHeader, styles.mobileHeaderHidden)
        }
      >
        <div className={styles.smallHeaderAlignment}>
          <div className={styles.logo}>
            <Link href="/">
              <Image
                src={"/Images/convertor-logo-sm.webp"}
                title="master sm logo"
                alt="Tiny Tools"
                width={38}
                height={38}
                sizes="38px"
                priority
              />
            </Link>
          </div>
          <div onClick={() => setMobileHeaderOpen(false)}>
            <X size={22} />
          </div>
        </div>
        <div className={styles.mobileViewBodyContentAlignment}>
          <div className={styles.mobileViewSearchWrap}>
            <Select
              components={{ IndicatorSeparator: () => null }}
              aria-label="search-tools"
              options={options}
              className={classNames("result-search-box", styles.mobileViewSearch)}
              classNamePrefix="select"
              placeholder={t("searchPlaceholder")}
              onChange={handleChange}
              styles={customStyles}
              isSearchable
            />
            {/* <PositiveTag /> */}
          </div>
          <div className={styles.mobileViewMoreLinks}>
            <Link
              href="/privacy-policy"
              className={styles.mobileViewMoreLink}
              onClick={() => setMobileHeaderOpen(false)}
            >
              <Shield size={18} />
              <span>{t("privacyPolicy")}</span>
            </Link>
            <Link
              href="/terms-condition"
              className={styles.mobileViewMoreLink}
              onClick={() => setMobileHeaderOpen(false)}
            >
              <FileText size={18} />
              <span>{t("termsOfService")}</span>
            </Link>
            <Link
              href="/faq"
              className={styles.mobileViewMoreLink}
              onClick={() => setMobileHeaderOpen(false)}
            >
              <HelpCircle size={18} />
              <span>{t("faq")}</span>
            </Link>
            <Link
              href="/contact"
              className={styles.mobileViewMoreLink}
              onClick={() => setMobileHeaderOpen(false)}
            >
              <Contact size={18} />
              <span>{t("contactUs")}</span>
            </Link>
          </div>
          <div className={styles.mobileViewrelativMainDiv}>
            <div
              className={styles.mobileViewcategoriseRelativeDiv}
              onClick={() => {
                setDropdownMobileOpen(!dropdownMobileOpen);
              }}
            >
              <span>{t("categories")}</span>
              {dropdownMobileOpen ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </div>
            <div
              className={
                dropdownMobileOpen
                  ? classNames(
                      styles.mobileViewdropdownDesign,
                      styles.mobileViewdropdownDesignOpen
                    )
                  : classNames(
                      styles.mobileViewdropdownDesign,
                      styles.mobileViewdropdownDesignClose
                    )
              }
            >
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => {
                  setMobileHeaderOpen(false);
                  // posthog.capture('draw_tool_opened', { source: 'mobile_menu' });
                  router.push("/draw");
                }}
              >
                <div>
                  <PenSquare size={20} />
                </div>
                <div>
                  <span>{t("draw")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#audio-tools")}
              >
                <div>
                  <Music size={20} />
                </div>
                <div>
                  <span>{t("audio")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#file-tools")}
              >
                <div>
                  <Folder size={20} />
                </div>
                <div>
                  <span>{t("file")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#text-tools")}
              >
                <div>
                  <Type size={20} />
                </div>
                <div>
                  <span>{t("text")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#number-tools")}
              >
                <div>
                  <Percent size={20} />
                </div>
                <div>
                  <span>{t("number")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#calculator-tools")}
              >
                <div>
                  <Calculator size={20} />
                </div>
                <div>
                  <span>{t("calculator")}</span>
                </div>
              </div>

              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#unit-tools")}
              >
                <div>
                  <Box size={20} />
                </div>
                <div>
                  <span>{t("unit")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#binary-tools")}
              >
                <div>
                  <FileText size={20} />
                </div>
                <div>
                  <span>{t("binary")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#development-tools")}
              >
                <div>
                  <FileCode size={20} />
                </div>
                <div>
                  <span>{t("development")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#proxy-tools")}
              >
                <div>
                  <Server size={20} />
                </div>
                <div>
                  <span>{t("proxy")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#other-tools")}
              >
                <div>
                  <Contact size={20} />
                </div>
                <div>
                  <span>{t("other")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#time-convert")}
              >
                <div>
                  <Clock size={20} />
                </div>
                <div>
                  <span>{t("timeConvert")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#currency-convert")}
              >
                <div>
                  <DollarSign size={20} />
                </div>
                <div>
                  <span>{t("currencyConvert")}</span>
                </div>
              </div>
              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#country-tools")}
              >
                <div>
                  <Globe size={20} />
                </div>
                <div>
                  <span>{t("country")}</span>
                </div>
              </div>

              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#social-media-tools")}
              >
                <div>
                  <Mail size={20} />
                </div>
                <div>
                  <span>{t("socialMedia")}</span>
                </div>
              </div>

              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#crypto-tools")}
              >
                <div>
                  <Bitcoin size={20} />
                </div>
                <div>
                  <span>{t("cryptoTools")}</span>
                </div>
              </div>

              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#language-tools")}
              >
                <div>
                  <Languages size={20} />
                </div>
                <div>
                  <span>{t("language")}</span>
                </div>
              </div>

              {/* <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#videodownload-tools")}
              >
                <div>
                  <i className="fa fa-download" aria-hidden="true"></i>
                </div>
                <div>
                  <span>{t("videoDownloader")}</span>
                </div>
              </div> */}

              <div
                className={styles.mobileViewallMenuAlignment}
                onClick={() => router.push("/#coder-tools")}
              >
                <div>
                  <FileCode size={20} />
                </div>
                <div>
                  <span>{t("coderTools")}</span>
                </div>
              </div>

              <div className={styles.mobileLangDropdown}>
                <span className={styles.mobileLangLabel}>{t("language")}</span>
                <div className={styles.mobileLangOptions}>
                  {localeOptions.map((opt) => (
                    <Link
                      key={opt.value}
                      href={pathname || "/"}
                      locale={opt.value}
                      className={classNames(styles.mobileLangOption, {
                        [styles.mobileLangOptionActive]: opt.value === locale,
                      })}
                      onClick={() => {
                        setMobileHeaderOpen(false);
                        // if (opt.value !== locale) {
                        //   posthog.capture('language_changed', {
                        //     from_locale: locale,
                        //     to_locale: opt.value,
                        //     source: 'mobile_menu',
                        //   });
                        // }
                      }}
                    >
                      {opt.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
